// src/share/types.ts
var ShareVisibility = {
  /** Only the creator (and admins) — effectively a private bookmark. */
  PRIVATE: "private",
  /** Members of the same customer/org/tenant. */
  INTERNAL: "internal",
  /** Anyone with the link (optionally gated by a password). */
  EXTERNAL: "external"
};
var ShareUpdateMode = {
  /** Resolves the latest data every time it's opened. */
  DYNAMIC: "dynamic",
  /** Frozen at creation; never updated with new info (stored as an S3 snapshot). */
  SNAPSHOT: "snapshot"
};
var SHARE_PATH = "/shares";
var SHARE_UPLOAD_TICKET_PATH = "/shares/upload-ticket";
var SHARE_STATE_PARAM = "share";

// src/share/state.ts
var nodeBuffer = globalThis.Buffer;
function base64UrlEncode(input) {
  const b64 = typeof btoa === "function" ? btoa(unescape(encodeURIComponent(input))) : nodeBuffer?.from(input, "utf-8").toString("base64") ?? "";
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function base64UrlDecode(input) {
  const b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - b64.length % 4) % 4);
  return typeof atob === "function" ? decodeURIComponent(escape(atob(padded))) : nodeBuffer?.from(padded, "base64").toString("utf-8") ?? "";
}
function withoutSecrets(input) {
  if (!input.options?.password) return input;
  const { password: _password, ...safeOptions } = input.options;
  return { ...input, options: safeOptions };
}
function encodeShareState(input) {
  return base64UrlEncode(JSON.stringify(withoutSecrets(input)));
}
function decodeShareState(token) {
  try {
    const parsed = JSON.parse(base64UrlDecode(token));
    return parsed && typeof parsed.resource === "string" ? parsed : null;
  } catch {
    return null;
  }
}
function defaultBaseUrl() {
  if (typeof window !== "undefined" && window.location) {
    return `${window.location.origin}${window.location.pathname}`;
  }
  return "";
}
function buildLocalShareUrl(input, baseUrl) {
  const base = baseUrl ?? defaultBaseUrl();
  return `${base}#${SHARE_STATE_PARAM}=${encodeShareState(input)}`;
}
function readShareStateFromUrl(url) {
  const hash = url ? new URL(url, "http://x").hash : typeof window !== "undefined" ? window.location.hash : "";
  const match = new RegExp(`${SHARE_STATE_PARAM}=([^&]+)`).exec(hash);
  const token = match?.[1];
  return token ? decodeShareState(token) : null;
}

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var sinks = [];
var minConsoleLevel = "warn";
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/share/createShareableLink.ts
var log = createLogger("fe-commons:share");
function shareRequestBody(input) {
  const opts = input.options ?? {};
  return {
    resource: input.resource,
    resource_id: input.resourceId,
    state: input.state,
    visibility: opts.visibility ?? ShareVisibility.INTERNAL,
    update_mode: opts.updateMode ?? ShareUpdateMode.DYNAMIC,
    password: opts.password,
    expires_in_seconds: opts.expiresInSeconds,
    allow_reshare: opts.allowReshare ?? false
  };
}
function localCannotHonour(opt) {
  const lost = [];
  if (opt.password) lost.push("a password");
  if (opt.updateMode === ShareUpdateMode.SNAPSHOT) lost.push("a frozen snapshot");
  if (opt.visibility && opt.visibility !== ShareVisibility.EXTERNAL) {
    lost.push(`${opt.visibility}-only access`);
  }
  if (opt.expiresInSeconds) lost.push("an expiry");
  return lost;
}
async function createShareableLink(input, opts = {}) {
  const opt = input.options ?? {};
  if (opts.client) {
    const res = await opts.client.post(opts.path ?? SHARE_PATH, shareRequestBody(input));
    if (res.ok) {
      if (!res.data?.url) {
        throw new Error("[share] mint succeeded but returned no URL");
      }
      return {
        url: res.data.url,
        token: res.data.token,
        expiresAt: res.data.expires_at,
        visibility: res.data.visibility ?? opt.visibility ?? ShareVisibility.INTERNAL,
        updateMode: res.data.update_mode ?? opt.updateMode ?? ShareUpdateMode.DYNAMIC,
        passwordProtected: res.data.password_protected ?? Boolean(opt.password),
        snapshotKey: res.data.snapshot_key,
        mode: "server"
      };
    }
    log.warn(`share mint failed (${res.error.error_code ?? res.statusCode}): ${res.error.message}`);
    if (opts.fallbackToLocal === false) {
      throw new Error(`[share] mint failed: ${res.error.message}`);
    }
  }
  const lost = localCannotHonour(opt);
  if (lost.length > 0) {
    throw new Error(
      `[share] cannot create this link without the sharing service \u2014 it would drop ${lost.join(", ")}. Try again, or share a public link instead.`
    );
  }
  return {
    url: buildLocalShareUrl(input, opts.baseUrl),
    // What was PRODUCED, not what was requested. A local link is an
    // unauthenticated URL; reporting the caller's narrower choice back to them is
    // how a public link came to be displayed as a private one.
    visibility: ShareVisibility.EXTERNAL,
    updateMode: ShareUpdateMode.DYNAMIC,
    passwordProtected: false,
    mode: "local"
  };
}

// src/share/s3.ts
var log2 = createLogger("fe-commons:share:s3");
async function requestUploadTicket(client, input, path = SHARE_UPLOAD_TICKET_PATH) {
  const res = await client.post(path, {
    resource: input.resource,
    resource_id: input.resourceId,
    content_type: input.contentType ?? "application/json",
    size_bytes: input.sizeBytes,
    visibility: input.visibility
  });
  if (!res.ok) throw new Error(`[share] upload ticket request failed: ${res.error.message}`);
  const d = res.data ?? {};
  if (!d.key || !d.upload_url && !d.post_url) {
    throw new Error("[share] upload ticket response missing key / upload URL");
  }
  return {
    key: d.key,
    bucket: d.bucket,
    uploadUrl: d.upload_url,
    postUrl: d.post_url,
    fields: d.fields,
    headers: d.headers,
    cdnUrl: d.cdn_url,
    expiresAt: d.expires_at
  };
}
async function uploadToTicket(ticket, body, opts = {}) {
  if (ticket.uploadUrl) {
    const headers = ticket.headers ?? (typeof body === "string" ? { "Content-Type": "application/json" } : void 0);
    const res = await fetch(ticket.uploadUrl, { method: "PUT", headers, body, signal: opts.signal });
    if (!res.ok) throw new Error(`[share] snapshot upload failed: HTTP ${res.status}`);
    return;
  }
  if (ticket.postUrl && ticket.fields) {
    const form = new FormData();
    for (const [k, v] of Object.entries(ticket.fields)) form.append(k, v);
    form.append("file", typeof body === "string" ? new Blob([body], { type: "application/json" }) : body);
    const res = await fetch(ticket.postUrl, { method: "POST", body: form, signal: opts.signal });
    if (!res.ok) throw new Error(`[share] snapshot upload failed: HTTP ${res.status}`);
    return;
  }
  throw new Error("[share] upload ticket has neither uploadUrl nor postUrl");
}
async function createSnapshotShare(client, input, opts = {}) {
  const body = input.snapshot ?? JSON.stringify(input.state ?? {});
  const contentType = input.contentType ?? (typeof body === "string" ? "application/json" : body.type || "application/octet-stream");
  const sizeBytes = typeof body === "string" ? body.length : body.size;
  const ticket = await requestUploadTicket(
    client,
    {
      resource: input.resource,
      resourceId: input.resourceId,
      contentType,
      sizeBytes,
      visibility: input.options?.visibility
    },
    opts.uploadTicketPath
  );
  await uploadToTicket(ticket, body, { signal: opts.signal });
  log2.debug(`uploaded snapshot for ${input.resource} to ${ticket.key}`);
  const registerInput = {
    ...input,
    options: { ...input.options, updateMode: ShareUpdateMode.SNAPSHOT }
  };
  const res = await client.post(opts.sharePath ?? SHARE_PATH, {
    ...shareRequestBody(registerInput),
    snapshot_key: ticket.key,
    snapshot_cdn_url: ticket.cdnUrl
  });
  if (!res.ok) throw new Error(`[share] snapshot share registration failed: ${res.error.message}`);
  if (!res.data?.url) throw new Error("[share] snapshot registered but returned no URL");
  return {
    url: res.data.url,
    token: res.data?.token,
    expiresAt: res.data?.expires_at,
    visibility: input.options?.visibility ?? ShareVisibility.INTERNAL,
    updateMode: ShareUpdateMode.SNAPSHOT,
    passwordProtected: res.data?.password_protected ?? Boolean(input.options?.password),
    snapshotKey: res.data?.snapshot_key ?? ticket.key,
    mode: "server"
  };
}
async function createRemoteSnapshotShare(client, input, opts = {}) {
  const registerInput = {
    ...input,
    options: { ...input.options, updateMode: ShareUpdateMode.SNAPSHOT }
  };
  const res = await client.post(opts.sharePath ?? SHARE_PATH, {
    ...shareRequestBody(registerInput),
    build_snapshot: true
  });
  if (!res.ok) throw new Error(`[share] remote snapshot share failed: ${res.error.message}`);
  if (!res.data?.url) throw new Error("[share] remote snapshot registered but returned no URL");
  return {
    url: res.data.url,
    token: res.data?.token,
    expiresAt: res.data?.expires_at,
    visibility: input.options?.visibility ?? ShareVisibility.INTERNAL,
    updateMode: ShareUpdateMode.SNAPSHOT,
    passwordProtected: res.data?.password_protected ?? Boolean(input.options?.password),
    snapshotKey: res.data?.snapshot_key,
    mode: "server"
  };
}

export { SHARE_PATH, SHARE_STATE_PARAM, SHARE_UPLOAD_TICKET_PATH, ShareUpdateMode, ShareVisibility, buildLocalShareUrl, createRemoteSnapshotShare, createShareableLink, createSnapshotShare, decodeShareState, encodeShareState, readShareStateFromUrl, requestUploadTicket, shareRequestBody, uploadToTicket };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map